// ============================================================================
// Sessions page — pure helpers (no React, no side effects).
// ----------------------------------------------------------------------------
// Date/time codecs for the <datetime-local> picker, the per-set volume formula
// (mirrors viccofitness/calories_svc.set_volume = reps × weight_kg), exercise
// name resolution by the active locale, and small null-safe formatters. Every
// helper is "optional-first": a missing input yields null / "" rather than a
// throw or a fake zero, matching the optional-first backend contract.
// ============================================================================
import type { CardioEntry, Exercise, SetEntry } from "../../api";
import { intlLocale } from "../../i18n/config";

// --- datetime <-> ISO -------------------------------------------------------

/** Pad to 2 digits. */
function p2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Encode a Date to the `YYYY-MM-DDTHH:MM` string an <input type=datetime-local>
 * wants. Local-time (no Z) — the value the user sees is the value we store.
 */
export function toDatetimeLocal(d: Date): string {
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}` +
    `T${p2(d.getHours())}:${p2(d.getMinutes())}`
  );
}

/** Now, rounded to the next quarter hour — a friendly default for new logs. */
export function defaultWhen(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const q = Math.ceil(d.getMinutes() / 15) * 15;
  d.setMinutes(q);
  return toDatetimeLocal(d);
}

/**
 * Parse a stored ISO timestamp into the <datetime-local> shape, or "" when
 * absent/unparseable. Strips any timezone so the field shows local wall-clock.
 */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return toDatetimeLocal(d);
}

/**
 * Turn a <datetime-local> value (device-local wall-clock) into the
 * timezone-aware UTC ISO-8601 string the API stores, or null when blank.
 *
 * Timezone convention: everything is stored as UTC. The picker yields a local
 * instant, so `new Date(localStr)` interprets it in the device timezone and
 * `toISOString()` converts to UTC. (Sending the naive local string as-is, the
 * previous behaviour, made the backend mis-stamp it as UTC — a session logged at
 * 10 PM in HK was read back ~8h off, on the wrong calendar day.)
 */
export function datetimeLocalToIso(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// --- formatting -------------------------------------------------------------

// These render STORED UTC timestamps in the device's local timezone (via
// `new Date(iso)`) using the ACTIVE i18n locale (via intlLocale) so month/
// weekday names localize under 繁中 instead of defaulting to the browser locale.

/** "16 Jun, 14:30" — date + time. null-safe (returns ""). */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(intlLocale(), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "Mon 16 Jun" — a day heading for grouped agendas. null-safe. */
export function fmtDayHeading(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "14:30" — just the time. null-safe. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(intlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Locale-aware number with fixed decimals. */
export function fmtNum(n: number, decimals = 0): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Money formatting lives in the shared lib/currency helper (formatMoney) so the
// trainer's HK$ / US$ / PS symbol convention is consistent across every page.

// --- exercise naming --------------------------------------------------------

/** Resolve an exercise's display name for the active locale (en fallback). */
export function exerciseName(
  ex: Exercise | undefined | null,
  locale: string,
): string {
  if (!ex) return "";
  const zh = locale.startsWith("zh");
  const primary = zh ? ex.name_zh : ex.name_en;
  const fallback = zh ? ex.name_en : ex.name_zh;
  return (primary || fallback || "").trim();
}

// --- volume -----------------------------------------------------------------

/**
 * Per-set training volume = reps × weight_kg (mirrors calories_svc.set_volume).
 * Returns null when either component is missing so the UI shows "–" not 0.
 */
export function setVolume(set: {
  reps?: number | null;
  weight_kg?: number | null;
}): number | null {
  if (set.reps == null || set.weight_kg == null) return null;
  const v = set.reps * set.weight_kg;
  return Number.isFinite(v) ? v : null;
}

/** Sum of computed (or locally-derived) set volumes, ignoring missing ones. */
export function totalVolume(sets: SetEntry[]): number {
  let sum = 0;
  for (const s of sets) {
    const v = s.volume ?? setVolume(s);
    if (v != null) sum += v;
  }
  return sum;
}

// --- numeric parsing for inputs ---------------------------------------------

/** Parse a numeric field value to a number, or null when blank/invalid. */
export function parseNum(v: string): number | null {
  const s = v.trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Parse to a non-negative integer, or null when blank/invalid. */
export function parseInt0(v: string): number | null {
  const n = parseNum(v);
  if (n == null) return null;
  const i = Math.trunc(n);
  return i >= 0 ? i : null;
}

/** Stringify a nullable number for a controlled input value ("" when null). */
export function numStr(n: number | null | undefined): string {
  return n == null ? "" : String(n);
}

// --- cardio summary ---------------------------------------------------------

/** A compact label for a cardio entry's metrics, e.g. "5 km · 28 min". */
export function cardioSummary(
  c: CardioEntry,
  units: { km: string; min: string; bpm: string },
): string {
  const parts: string[] = [];
  if (c.distance != null) parts.push(`${fmtNum(c.distance, 2)} ${units.km}`);
  if (c.time_min != null) parts.push(`${fmtNum(c.time_min)} ${units.min}`);
  if (c.avg_hr != null) parts.push(`${fmtNum(c.avg_hr)} ${units.bpm}`);
  return parts.join(" · ");
}
